import React, { useState } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Form, Button, Card, Alert, Spinner, Table } from 'react-bootstrap';
import { apiRequest } from '../../api/client';
import { endpoints } from '../../api/endpoints';

const CasSearchPage = () => {
    const [query, setQuery] = useState('');
    const [searchResults, setSearchResults] = useState(null);
    const [selectedDetail, setSelectedDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!query.trim()) return;

        setLoading(true);
        setError(null);
        setSearchResults(null);
        setSelectedDetail(null);

        try {
            // Check if it looks like a CAS RN (numbers and dashes)
            const isCasRn = /^\d+-\d+-\d+$/.test(query.trim());

            if (isCasRn) {
                const data = await apiRequest(`${endpoints.casSearch}?casNumber=${encodeURIComponent(query.trim())}`);
                setSelectedDetail(data);
            } else {
                const data = await apiRequest(`${endpoints.casQuery}?q=${encodeURIComponent(query.trim())}`);
                setSearchResults(data);
            }
        } catch (err) {
            console.error("CAS Error:", err);
            setError(err.message || "Failed to fetch data. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const fetchDetail = async (casRn) => {
        setDetailLoading(true);
        setError(null);
        try {
            const data = await apiRequest(`${endpoints.casSearch}?casNumber=${encodeURIComponent(casRn)}`);
            setSelectedDetail(data);
        } catch (err) {
            console.error("Detail Error:", err);
            setError("Failed to fetch details for " + casRn);
        } finally {
            setDetailLoading(false);
        }
    };

    const renderValue = (value) => {
        if (typeof value === 'object' && value !== null) {
            return <pre style={{ whiteSpace: 'pre-wrap', maxHeight: '200px', overflow: 'auto' }}>{JSON.stringify(value, null, 2)}</pre>;
        }
        return String(value);
    };

    return (
        <AdminLayout title="CAS Registry Search" subtitle="Search by CAS RN, Name, or Grade to retrieve properties and Molecular Mass">
            <Card className="mb-4 shadow-sm">
                <Card.Body>
                    <Form onSubmit={handleSearch}>
                        <Form.Group className="mb-3" controlId="formCasQuery">
                            <Form.Label>CAS Number or Chemical Name/Grade</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="e.g., 50-00-0 or Formaldehyde or PCL"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                            />
                            <Form.Text className="text-muted">
                                Enter a CAS Registry Number for direct results, or a name to search for matching records.
                            </Form.Text>
                        </Form.Group>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Search'}
                        </Button>
                    </Form>
                </Card.Body>
            </Card>

            {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

            {/* General Search Results */}
            {searchResults && searchResults.results && (
                <Card className="mb-4 shadow-sm">
                    <Card.Header className="bg-light"><strong>Matching Substances ({searchResults.count})</strong></Card.Header>
                    <Card.Body>
                        <Table striped hover responsive className="mb-0">
                            <thead>
                                <tr>
                                    <th>CAS RN</th>
                                    <th>Name</th>
                                    <th>Image</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {searchResults.results.map((item) => (
                                    <tr key={item.rn}>
                                        <td><code>{item.rn}</code></td>
                                        <td><div dangerouslySetInnerHTML={{ __html: item.name }} /></td>
                                        <td>
                                            {item.image && (
                                                <img
                                                    src={`https://commonchemistry.cas.org/api/image?rn=${item.rn}`}
                                                    alt={item.rn}
                                                    style={{ height: '40px' }}
                                                />
                                            )}
                                        </td>
                                        <td>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => fetchDetail(item.rn)}
                                                disabled={detailLoading}
                                            >
                                                Get Details
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            )}

            {/* Detailed Properties */}
            {(selectedDetail || detailLoading) && (
                <Card className="shadow-sm">
                    <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                        <span><strong>Detailed Properties {selectedDetail?.rn ? `- ${selectedDetail.rn}` : ''}</strong></span>
                        {selectedDetail?.rn && (
                            <img
                                src={`https://commonchemistry.cas.org/api/image?rn=${selectedDetail.rn}`}
                                alt="Structure"
                                style={{ height: '30px', filter: 'brightness(0) invert(1)' }}
                            />
                        )}
                    </Card.Header>
                    <Card.Body>
                        {detailLoading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-2">Fetching properties...</p>
                            </div>
                        ) : (
                            <>
                                {selectedDetail.molecularMass && (
                                    <Alert variant="info">
                                        <h5 className="mb-0">Molecular Mass: <strong>{selectedDetail.molecularMass}</strong></h5>
                                    </Alert>
                                )}
                                <Table striped bordered hover responsive>
                                    <thead>
                                        <tr>
                                            <th>Property</th>
                                            <th>Value</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Object.entries(selectedDetail).map(([key, value]) => (
                                            <tr key={key}>
                                                <td className="fw-bold text-capitalize">{key.replace(/([A-Z])/g, ' $1')}</td>
                                                <td>{renderValue(value)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </>
                        )}
                    </Card.Body>
                </Card>
            )}
        </AdminLayout>
    );
};

export default CasSearchPage;
